import { useState, useMemo } from 'react';
import { PlusCircle, Pencil, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useGroundRent, useGroundRentPayments } from '@/hooks/useGroundRent';
import { GroundRentForm } from './GroundRentForm';
import { GroundRentPaymentForm } from './GroundRentPaymentForm';
import { FixedAsset } from '@/hooks/useFixedAssets';
import { ExternalLink, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteGroundRentPayment } from '@/hooks/useGroundRent';
import { useDeleteTransaction, useCreateTransaction } from '@/hooks/useTransactions';
import { useFetchCategories } from '@metadataHooks';
import { useFetchSubCategories } from '@metadataHooks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function GroundRentSection({ asset }: { asset: FixedAsset }) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentYear, setPaymentYear] = useState<number | undefined>();
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [deleteTxId, setDeleteTxId] = useState<string | null>(null);

  const deletePayment = useDeleteGroundRentPayment();
  const deleteTransaction = useDeleteTransaction();
  const createTransaction = useCreateTransaction();
  
  const { data: categories = [] } = useFetchCategories();
  const otherCategory = categories.find((c) => c.name.toLowerCase() === 'other');
  const { data: subCategories = [] } = useFetchSubCategories(otherCategory?.id);
  const refundSubCat = subCategories.find((sc) => sc.name.toLowerCase() === 'refunds');

  const { data: record, isLoading: recordLoading } = useGroundRent(asset.id);
  const { data: payments = [] } = useGroundRentPayments(record?.id);

  const paymentYears = useMemo(() => new Set(payments.map(p => p.year_paid)), [payments]);
  
  // Calculate missing years
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentDay = new Date().getDate();

  const missingYears = useMemo(() => {
    if (!record) return [];
    const missing = [];
    for (let year = record.start_year; year <= currentYear; year++) {
      if (!paymentYears.has(year)) {
        // If it's the current year, check if it's past due
        if (year === currentYear) {
          if (currentMonth > record.due_month || (currentMonth === record.due_month && currentDay >= record.due_day)) {
            missing.push({ year, overdue: true });
          } else {
            missing.push({ year, overdue: false }); // Upcoming
          }
        } else {
          missing.push({ year, overdue: true }); // Past years
        }
      }
    }
    // Sort descending so most recent is first
    return missing.sort((a, b) => b.year - a.year);
  }, [record, paymentYears, currentYear, currentMonth, currentDay]);

  if (recordLoading) return null;

  if (!record) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle>Ground Rent</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Set Up Ground Rent
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No ground rent obligation is configured for this property. If ground rent is applicable, set it up to start tracking annual payments.
          </p>
        </CardContent>

        <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Up Ground Rent</DialogTitle>
            </DialogHeader>
            <GroundRentForm fixedAssetId={asset.id} assetCurrency={asset.currency} onSuccess={() => setSetupOpen(false)} />
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  const handlePayClick = (year?: number) => {
    setPaymentYear(year);
    setPaymentOpen(true);
  };

  const handleRecordRefund = async () => {
    if (!deletePaymentId) return;
    try {
      if (deleteTxId) {
        // Fetch the original transaction to get account, amount, and currency
        const { data: tx, error } = await supabase
          .from('transactions')
          .select('account_id, amount, currency')
          .eq('id', deleteTxId)
          .single();
        
        if (error || !tx) throw error || new Error('Transaction not found');
        if (!otherCategory) throw new Error('Other category not found');

        await createTransaction.mutateAsync({
          account_id: tx.account_id,
          type: 'income',
          amount: tx.amount,
          transaction_date: new Date().toISOString().split('T')[0],
          category_id: otherCategory.id,
          sub_category_id: refundSubCat?.id || null,
          currency: tx.currency,
          description: `Refund for Ground Rent Payment`,
          notes: null,
          fee: 0,
        });
      }
      
      await deletePayment.mutateAsync({ paymentId: deletePaymentId });
      toast.success('Payment deleted and refund recorded');
    } catch (error: any) {
      toast.error(`Failed to process refund: ${error.message}`);
    } finally {
      setDeletePaymentId(null);
      setDeleteTxId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!deletePaymentId) return;
    try {
      if (deleteTxId) {
        await deleteTransaction.mutateAsync(deleteTxId);
      }
      await deletePayment.mutateAsync({ paymentId: deletePaymentId });
      toast.success('Payment deleted successfully');
    } catch (error: any) {
      toast.error(`Failed to delete payment: ${error.message}`);
    } finally {
      setDeletePaymentId(null);
      setDeleteTxId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Ground Rent</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Terms
          </Button>
          <Button size="sm" onClick={() => handlePayClick()}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Record Payment
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Terms Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <p className="text-sm text-muted-foreground">Annual Amount</p>
            <p className="font-semibold">{formatCurrency(Number(record.annual_amount), (record as any).currency || asset.currency)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Due Every</p>
            <p className="font-medium">
              {new Date(2000, record.due_month - 1, record.due_day).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Tracking Since</p>
            <p className="font-medium">{record.start_year}</p>
          </div>
          <div className="flex flex-col items-start justify-center">
             {missingYears.filter(m => m.overdue).length > 0 ? (
               <Badge variant="destructive" className="flex items-center gap-1">
                 <AlertCircle className="h-3 w-3" />
                 {missingYears.filter(m => m.overdue).length} Overdue
               </Badge>
             ) : (
               <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                 All Caught Up
               </Badge>
             )}
          </div>
        </div>

        {/* Missing / Upcoming */}
        {missingYears.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Action Required</h4>
            <div className="space-y-2">
              {missingYears.map(({ year, overdue }) => (
                <div key={year} className="flex items-center justify-between p-3 border rounded-md bg-card">
                  <div className="flex items-center gap-3">
                    {overdue ? (
                      <Badge variant="destructive">Overdue</Badge>
                    ) : (
                      <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">Upcoming</Badge>
                    )}
                    <span className="font-medium">{year} Ground Rent</span>
                  </div>
                  <Button size="sm" variant={overdue ? "default" : "outline"} onClick={() => handlePayClick(year)}>
                    Pay {formatCurrency(Number(record.annual_amount), asset.currency)}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment History Table */}
        {payments.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Payment History</h4>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Year Paid</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.year_paid}</TableCell>
                      <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                      <TableCell>{p.receipt_number || '—'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(Number(p.amount), asset.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {(p as any).transaction_id && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/transactions/${(p as any).transaction_id}`}>
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-destructive"
                            onClick={() => {
                              setDeletePaymentId(p.id);
                              setDeleteTxId((p as any).transaction_id || null);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Ground Rent Terms</DialogTitle>
          </DialogHeader>
          <GroundRentForm fixedAssetId={asset.id} assetCurrency={asset.currency} existingRecord={record} onSuccess={() => setSetupOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={paymentOpen} onOpenChange={(open) => {
        setPaymentOpen(open);
        if (!open) setPaymentYear(undefined);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Ground Rent Payment</DialogTitle>
          </DialogHeader>
          <GroundRentPaymentForm 
            groundRentId={record.id} 
            paymentCurrency={(record as any).currency || asset.currency}
            defaultYear={paymentYear}
            expectedAmount={Number(record.annual_amount)}
            onSuccess={() => {
              setPaymentOpen(false);
              setPaymentYear(undefined);
            }} 
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ground Rent Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ground rent payment?
              {deleteTxId && " This payment is linked to a transaction."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {deleteTxId && (
              <Button 
                variant="outline" 
                onClick={handleRecordRefund}
                disabled={deletePayment.isPending || createTransaction.isPending}
              >
                {deletePayment.isPending || createTransaction.isPending ? 'Processing...' : 'Record Refund'}
              </Button>
            )}
            <Button 
              variant="destructive" 
              onClick={handleDeleteAll}
              disabled={deletePayment.isPending || deleteTransaction.isPending}
            >
              {deletePayment.isPending || deleteTransaction.isPending ? 'Deleting...' : (deleteTxId ? 'Delete Transaction & Payment' : 'Delete Payment')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
